import { Experiment } from '@naturalcycles/shared'
import { SupportedLocale } from '@src/cnst/translations.cnst'
import { OrphanRemover } from '@src/components/orphan-remover/OrphanRemover.component'
import { Breakpoints } from '@src/hooks/useBreakpoint'
import { useTestStatus } from '@src/hooks/useTestStatus'
import { useWindowDimensions } from '@src/hooks/useWindowDimensions'
import { DatoCmsMedia } from '@src/typings/dato-cms-assets'
import classNames from 'classnames'
import { type ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { ClientOnly } from '../client-only/ClientOnly.component'
import { HeroBadge } from '../hero-badge/HeroBadge.component'
import { SignupButton } from '../signup-button/SignupButton.component'
import style from './HomepageHero.module.scss'

export interface HomepageHeroProps {
  heading: string
  context: string
  cta: string
  footnote: string
  media: DatoCmsMedia
  ratings: {
    rating: string
    storeName: string
    context: string
  }
}

export interface HomepageHeroHeadProps {
  medias: DatoCmsMedia[]
}

export function HomepageHeroHead({ medias }: HomepageHeroHeadProps): ReactNode {
  const { width } = useWindowDimensions()
  return (
    <Helmet>
      {medias.map(media => [
        <link
          key={`preload-${media.url}`}
          rel="preload"
          as="media"
          href={media.url}
          type="video/mp4"
        />,
        <link
          key={`poster-preload-${media.url}`}
          rel="preload"
          as="image"
          href={getThumbnailUrl(media, width)}
        />,
      ])}
    </Helmet>
  )
}

export function HomepageHero({
  media,
  heading,
  context,
  cta,
  footnote,
  ratings,
}: HomepageHeroProps): ReactNode {
  const { width } = useWindowDimensions()
  const isMobile = !width || width < Breakpoints.bpMedium
  const videoThumbnailUrl = getThumbnailUrl(media, width)
  const [AB373] = useTestStatus(Experiment.NCWEB_HERO_BADGES_V2, false, false, [
    SupportedLocale.enUS,
  ])
  const isTest373 = AB373 === 'test'

  return (
    <section
      className={classNames(style.HomepageHero, {
        [style.HomepageHero___test373]: isTest373,
      })}
    >
      <Helmet>
        <link rel="preload" as="media" href={media.url} type="video/mp4" />
        <link rel="preload" as="image" href={videoThumbnailUrl} />
      </Helmet>
      <ClientOnly>
        <div className={style.HomepageHero__overlay} />
        <div className={style.HomepageHero__videoContainer}>
          <video
            className={style.HomepageHero__video}
            poster={videoThumbnailUrl}
            autoPlay
            playsInline
            muted
            loop
          >
            <source src={media.url} type="video/mp4" title={media.title} />
          </video>
        </div>

        {isTest373 && isMobile && (
          <div className={style.HomepageHero__keepYourDataSafe}>
            <img
              src="/icons/fsa-badge.svg"
              className={style.HomepageHero__keepYourDataSafe__icon}
              alt=""
            />
            <div className={style.HomepageHero__keepYourDataSafe__text}>{footnote}</div>
          </div>
        )}

        <div className={style.HomepageHero__container}>
          {!isTest373 && (
            <HeroBadge
              rating={ratings.rating}
              store={ratings.storeName}
              context={ratings.context}
            />
          )}
          <h1>
            <OrphanRemover textBlock={heading} />
          </h1>
          <p dangerouslySetInnerHTML={{ __html: context }} />
          <div className={style.HomepageHero__cta}>
            <SignupButton
              ncUtmSetSource="home_page_hero_signup_button"
              uid="home-page-hero-signup-button"
              size="flexible"
            >
              {cta}
            </SignupButton>

            {(!isTest373 || (isTest373 && !isMobile)) && (
              <div className={style.HomepageHero__keepYourDataSafe}>
                <img
                  alt=""
                  src="/icons/fsa-badge.svg"
                  className={style.HomepageHero__keepYourDataSafe__icon}
                />
                <div className={style.HomepageHero__keepYourDataSafe__text}>{footnote}</div>
              </div>
            )}
          </div>
        </div>
      </ClientOnly>
    </section>
  )
}

function getThumbnailUrl(media: DatoCmsMedia, width: number | null): string {
  return `${media.url}?width=${width}&time=0`
}
